// ChatroomItem.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { format } from 'date-fns';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import UserAvatarWithStatus from '../container/UserAvatarWithStatus.js';
import { ButtonId } from '../resource/trackingConstants.js';
import {
  CHAT_ID as AUTO_CHAT_CHAT_ID,
  USERNAME_LOKALISE_KEY as AUTO_CHAT_USERNAME_LOKALISE_KEY,
} from '../resource/autoChatConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import PinGreyIcon from '../../img/ic_pin_grey.svg';
import { color } from '../style/variables.js';

const MSEC_IN_ONE_DAY = 3600 * 24 * 1000;
const MSEC_IN_ONE_YEAR = 365 * MSEC_IN_ONE_DAY;

export class ChatroomItem extends React.PureComponent {
  fetchMediaMenifestOfMessage = () => {
    const { isIntersecting, lastMessageId, fetchMediaMenifestOfMessage } =
      this.props;
    if (isIntersecting) {
      fetchMediaMenifestOfMessage(lastMessageId);
    }
  };

  fetchMessageDetail = () => {
    const { isIntersecting, lastMessageId, fetchMessageDetail } = this.props;
    if (!isIntersecting) {
      return;
    }
    return fetchMessageDetail({ id: lastMessageId });
  };

  getFormattedPostedAt = () => {
    const { postedAtUnix } = this.props;
    if (!postedAtUnix) return '';

    const postedAt = postedAtUnix * 1000;
    const timeOffset = Date.now() - postedAt;
    const dateFormat =
      MSEC_IN_ONE_YEAR < timeOffset
        ? 'yyyy/MM/dd'
        : MSEC_IN_ONE_DAY < timeOffset
          ? 'MM/dd'
          : 'HH:mm';
    return format(postedAt, dateFormat);
  };

  getProcessCaption() {
    const { assetIds, caption, type, isMe, mediaType, t } = this.props;
    const isMultiAssets = assetIds.length > 1;
    if (isMultiAssets) {
      return t(
        isMe
          ? 'chat_multiple_assets_message_sent'
          : 'chat_multiple_assets_message_received',
        {
          ns: TranslationNamespace.CHAT,
        }
      );
    } else if (type === 'gift') {
      return isMe
        ? t('gift_sent', {
            ns: TranslationNamespace.CHAT,
          }).replace(':gift:', '\ud83c\udf81')
        : t('gift_received', {
            ns: TranslationNamespace.CHAT,
          }).replace(':gift:', '\ud83c\udf81');
    } else if (mediaType === 'image') {
      return isMe
        ? t('photo_send', {
            ns: TranslationNamespace.CHAT,
          })
        : t('photo_receive', {
            ns: TranslationNamespace.CHAT,
          });
    } else if (mediaType === 'video') {
      return isMe
        ? t('video_send', {
            ns: TranslationNamespace.CHAT,
          })
        : t('video_receive', {
            ns: TranslationNamespace.CHAT,
          });
    } else if (mediaType === 'audio') {
      return isMe
        ? t('voice_message_send', {
            ns: TranslationNamespace.CHAT,
          })
        : t('voice_message_receive', {
            ns: TranslationNamespace.CHAT,
          });
    } else if (caption) {
      return caption;
    } else {
      return '';
    }
  }

  getIsAutoChatFeed = () => {
    const { id } = this.props;
    return id === AUTO_CHAT_CHAT_ID;
  };

  componentDidMount() {
    this.fetchMediaMenifestOfMessage();
    this.fetchMessageDetail();
  }

  componentDidUpdate(prevProps) {
    const { isIntersecting } = this.props;
    if (isIntersecting !== prevProps.isIntersecting) {
      this.fetchMediaMenifestOfMessage();
      this.fetchMessageDetail();
    }
  }

  render() {
    const {
      id,
      participantId,
      participantName,
      participantDisplayName,
      unreadCount,
      unreadGiftCount,
      hasUnread,
      isDisplayNameEnabled,
      isActive,
      isPinned,
      t,
    } = this.props;

    const totalUnreadCount = unreadCount + unreadGiftCount;
    const participantUsername = this.getIsAutoChatFeed()
      ? t(AUTO_CHAT_USERNAME_LOKALISE_KEY, {
          ns: TranslationNamespace.CHAT,
        })
      : participantName
        ? participantName
        : '-';

    return (
      <StyledChatroomItem
        isAutoChatFeed={this.getIsAutoChatFeed()}
        isActive={isActive}
        to={`/chat/${id}`}
        data-element_id={
          this.getIsAutoChatFeed() && ButtonId.Chatroom.ButtonChatFeed
        }
      >
        <AvatarLink
          isAutoChatFeed={this.getIsAutoChatFeed()}
          onClick={e => {
            e.preventDefault();
          }}
        >
          <UserAvatarWithStatus
            id={participantId}
            size={48}
            circleOffset={2}
            imageStyle={{ backgroundColor: '#232323' }}
            shouldShowLiveStream
            shouldShowOnline
          />
        </AvatarLink>
        <BodyLink>
          <Content>
            <TextWrapper>
              <Username>
                {isPinned && <PinImage src={PinGreyIcon} alt="pin icon" />}
                <NameWrapper>
                  {isDisplayNameEnabled && participantDisplayName ? (
                    <>
                      {participantDisplayName}
                      <SecondaryText>@{participantUsername}</SecondaryText>
                    </>
                  ) : (
                    '@' + participantUsername
                  )}
                </NameWrapper>
              </Username>
              <Caption hasUnread={hasUnread}>
                {this.getProcessCaption() && this.getFormattedPostedAt() && (
                  <>
                    <MessageText>{this.getProcessCaption()}</MessageText>
                    <PostedAt>{this.getFormattedPostedAt()}</PostedAt>
                  </>
                )}
              </Caption>
            </TextWrapper>
          </Content>
          <Metadata>
            <UnreadCountWrapper>
              <UnreadCount hasUnread={hasUnread} hasGift={unreadGiftCount > 0}>
                {this.getIsAutoChatFeed()
                  ? 'N'
                  : totalUnreadCount > 99
                    ? '99+'
                    : totalUnreadCount}
              </UnreadCount>
            </UnreadCountWrapper>
          </Metadata>
        </BodyLink>
      </StyledChatroomItem>
    );
  }
}

ChatroomItem.propTypes = {
  id: PropTypes.string,
  lastMessageId: PropTypes.string,
  participantId: PropTypes.string,
  participantName: PropTypes.string,
  participantDisplayName: PropTypes.string,
  type: PropTypes.string,
  caption: PropTypes.string,
  mediaType: PropTypes.string,
  isDisplayNameEnabled: PropTypes.bool,
  isMe: PropTypes.bool,
  hasUnread: PropTypes.bool,
  isIntersecting: PropTypes.bool,
  isActive: PropTypes.bool,
  isPinned: PropTypes.bool,
  unreadCount: PropTypes.number,
  unreadGiftCount: PropTypes.number,
  postedAtUnix: PropTypes.number,
  assetIds: PropTypes.array,
  fetchMediaMenifestOfMessage: PropTypes.func,
  fetchMessageDetail: PropTypes.func,
  t: PropTypes.func.isRequired,
};

ChatroomItem.defaultProps = {
  id: '',
  lastMessageId: '',
  mediaType: '',
  type: null,
  isDisplayNameEnabled: true,
  isMe: false,
  hasUnread: false,
  isIntersecting: false,
  isActive: false,
  isPinned: false,
  unreadCount: 0,
  unreadGiftCount: 0,
  postedAtUnix: 0,
  assetIds: [],
  fetchMediaMenifestOfMessage: () => null,
  fetchMessageDetail: () => null,
};

// Omit props for Link to prevent unknown props warnings
// Ref: https://github.com/styled-components/styled-components/issues/305#issuecomment-278132132

const StyledChatroomItem = styled(
  ({ hasUnread, isAutoChatFeed, isActive, children, ...restProps }) => (
    <Link {...restProps}>{children}</Link>
  )
)`
  display: flex;
  align-items: center;
  background-color: ${({ isAutoChatFeed, isActive }) =>
    isAutoChatFeed || isActive ? color.grey['900'] : color.black};
  padding: 12px 16px;
  margin-right: 5px;
  margin-left: 5px;
  border-radius: 4px;
`;

const AvatarLink = styled.div`
  ${({ isAutoChatFeed }) =>
    isAutoChatFeed ? 'border-bottom: 1px solid rgba(100, 100, 100, 0.5);' : ''};
  flex: none;
  position: relative;
`;

const PinImage = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 2px;
`;

const BodyLink = styled.div`
  flex: auto;
  display: flex;
  align-items: center;
  height: 100%;
  margin-left: 12px;
`;

const Content = styled.div`
  flex: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
`;

const TextWrapper = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
`;

const Username = styled.div`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  font-size: 16px;
`;

const NameWrapper = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--font-color-primary-on-dark, #fff);
  flex-grow: 1;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: 0.024px;
`;

const SecondaryText = styled.span`
  color: var(--font-color-secondary-on-dark, rgba(221, 221, 221, 0.8));
  padding-left: 2px;
  font-weight: 400;
`;

const Caption = styled.div`
  line-height: 1;
  min-height: 12px;
  font-size: 12px;
  display: flex;
  color: ${({ hasUnread }) => (hasUnread ? '#fff' : '#646464')};
`;

const MessageText = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Metadata = styled.div`
  flex: none;
  margin-left: 19px;
`;

const PostedAt = styled.div`
  ::before {
    content: '・';
    margin-left: 2px;
    margin-right: 2px;
  }
  display: inherit;
  line-height: 1;
`;

const UnreadCountWrapper = styled.div`
  text-align: right;
`;

const UnreadCount = styled.div`
  display: inline-block;
  position: relative;
  border-radius: 11px;
  background-color: #00d2be;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  min-width: 20px;
  height: 20px;
  line-height: 20px;
  padding: 0 6px;
  text-align: center;
  visibility: ${({ hasUnread }) => (hasUnread ? 'visible' : 'hidden')};
  &::before {
    ${({ hasGift }) => (hasGift ? 'content: "";' : null)};
    position: absolute;
    top: -3px;
    right: -3px;
    background-color: #ff7878;
    border: 2px solid #191919;
    border-radius: 50%;
    width: 10px;
    height: 10px;
  }
`;

export default withTranslation()(ChatroomItem);
